import { z } from 'zod';
import { IdSchema, InstantSchema, LocalDateSchema, MoneySchema } from '../common/index.js';
import { InvoiceLineSchema } from './devis.js';

/**
 * Facturation et paiements (architecture §2.7). Côté client (RG-17), aucune somme réglée n'est exposée :
 * « Vous êtes à jour » ou le seul reste dû, et un lien vers les reçus.
 */

export const BillingRuleItemSchema = z
  .object({
    ruleCode: z.enum([
      'forfait_phase',
      'repartition_quote_part',
      'frais_reels',
      'avance_heritier',
      'echeancier',
      'reglement_avoirs_notaire',
      'remise',
    ]),
    enabled: z.boolean(),
    params: z.record(z.string(), z.unknown()),
  })
  .meta({ id: 'BillingRuleItem' });

export const BillingRuleSetSchema = z
  .object({ versionNo: z.number().int().min(1), reason: z.string(), createdAt: InstantSchema, items: z.array(BillingRuleItemSchema) })
  .meta({ id: 'BillingRuleSet', description: 'Jeu de règles versionné du dossier.' });

export const BillingRuleSetCreateSchema = z
  .object({ reason: z.string().min(3), items: z.array(BillingRuleItemSchema).min(1) })
  .meta({ id: 'BillingRuleSetCreate' });

export const InvoiceSchema = z
  .object({
    id: IdSchema,
    number: z.string().regex(/^MAAT-F-\d{4}-\d{4}$/),
    issuedAt: InstantSchema,
    payerPersonId: IdSchema,
    total: MoneySchema,
    status: z.enum(['emise', 'payee', 'partiellement_payee', 'annulee_par_avoir']),
    /** Lignes détaillées (30/10/2026) : données par la vue de l'équipe ; absentes de la réponse d'émission. */
    lines: z.array(InvoiceLineSchema).optional(),
  })
  .meta({ id: 'Invoice', description: 'Facture émise, immuable (corrigée par avoir).' });

export const InvoiceIssueSchema = z.object({ payerPersonId: IdSchema }).meta({ id: 'InvoiceIssue' });

export const PaymentSchema = z
  .object({
    id: IdSchema,
    personId: IdSchema,
    amount: MoneySchema,
    status: z.enum(['en_attente', 'reussi', 'echoue', 'rembourse']),
    paidAt: InstantSchema.nullable(),
    provider: z.enum(['stripe', 'gocardless', 'manual']),
  })
  .meta({ id: 'Payment' });

export const HeirBillingLineSchema = z
  .object({
    personId: IdSchema,
    displayName: z.string().min(1),
    due: MoneySchema,
    paid: MoneySchema,
    remaining: MoneySchema,
    status: z.enum(['a_jour', 'echeancier', 'impaye', 'remise']),
    blocksSignature: z.boolean(),
  })
  .meta({ id: 'HeirBillingLine' });

export const BillingStaffViewSchema = z
  .object({
    dossierId: IdSchema,
    ruleSet: BillingRuleSetSchema,
    perHeir: z.array(HeirBillingLineSchema),
    invoices: z.array(InvoiceSchema),
    payments: z.array(PaymentSchema),
    collected: MoneySchema,
    outstanding: MoneySchema,
    notaryFeesCollectedByCabinet: z.literal(false),
  })
  .meta({ id: 'BillingStaffView', description: 'Facturation détaillée du dossier (équipe ; Brigitte sans montants côté pilotage).' });

export const InstallmentSchema = z
  .object({
    id: IdSchema,
    personId: IdSchema,
    amount: MoneySchema,
    dueAt: LocalDateSchema,
    isManualOverride: z.boolean(),
    status: z.enum(['a_venir', 'payee', 'en_retard']),
  })
  .meta({ id: 'Installment' });

export const CollectionScheduleSchema = z
  .object({
    dossierId: IdSchema,
    signatureDate: LocalDateSchema,
    marginDays: z.number().int().min(0),
    deadline: LocalDateSchema,
    installments: z.array(InstallmentSchema),
  })
  .meta({ id: 'CollectionSchedule', description: 'Calendrier de collecte avant la signature chez le notaire.' });

export const InstallmentPlanCreateSchema = z
  .object({ personId: IdSchema, total: MoneySchema, count: z.number().int().min(1).max(12) })
  .meta({ id: 'InstallmentPlanCreate' });

export const PaymentLinkRequestSchema = z
  .object({ installmentIds: z.array(IdSchema).min(1), provider: z.enum(['stripe', 'gocardless']) })
  .meta({ id: 'PaymentLinkRequest' });

export const PaymentLinkSchema = z
  .object({ id: IdSchema, url: z.string().min(1), expiresAt: InstantSchema, provider: z.enum(['stripe', 'gocardless']) })
  .meta({ id: 'PaymentLink' });

export const BillingClientViewSchema = z
  .strictObject({
    upToDate: z.boolean(),
    remainingDue: MoneySchema.nullable(),
    nextInstallment: z.strictObject({ dueAt: LocalDateSchema, amount: MoneySchema }).nullable(),
    receiptsUrl: z.string().min(1),
  })
  .refine((v) => v.upToDate === (v.remainingDue === null), {
    message: '« Vous êtes à jour » et un reste dû sont exclusifs',
    path: ['remainingDue'],
  })
  .meta({ id: 'BillingClientView', description: 'Vue client : « Vous êtes à jour » ou le reste dû, jamais un montant réglé.' });
